import type { Difficulty, FillQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-2：加減直式計算，含加法多次進位。
 * difficulty 1：三位數+三位數（單次進位）
 * difficulty 2：三位數+三位數（多次進位）
 * difficulty 3：四位數+三/四位數（多次進位）
 */
export function generateAddCarry(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);

  let a: number;
  let b: number;

  if (difficulty === 1) {
    // Ensure exactly one carry: ones place sums to ≥ 10, tens place does not
    do {
      const onesA = randInt(rand, 5, 9);
      const onesB = randInt(rand, 10 - onesA, 9); // force ones carry
      const tensA = randInt(rand, 1, 4);
      const tensB = randInt(rand, 1, 4); // tens sum < 10 (no carry)
      const hundA = randInt(rand, 1, 4);
      const hundB = randInt(rand, 1, 4);
      a = hundA * 100 + tensA * 10 + onesA;
      b = hundB * 100 + tensB * 10 + onesB;
    } while (a + b >= 1000); // stay under 1000 for d1
  } else if (difficulty === 2) {
    // Multiple carries: ones/tens/hundreds 全進位(建構式保證,迴圈只當保險)。
    // ⚠ 2026-06-11 根治死迴圈:原本 hund 取 1..4,兩數和最大 998,卻要求
    // a+b ≥ 1000 → 條件永遠不成立、無限迴圈(錯題本「再練一題」整頁凍死)。
    // 改成 hundB ≥ 10-hundA 強制百位也進位,a+b 必在 1110..1998。
    do {
      const onesA = randInt(rand, 6, 9);
      const onesB = randInt(rand, 10 - onesA, 9);
      const tensA = randInt(rand, 5, 9);
      const tensB = randInt(rand, 10 - tensA, 9);
      const hundA = randInt(rand, 5, 9);
      const hundB = randInt(rand, 10 - hundA, 9);
      a = hundA * 100 + tensA * 10 + onesA;
      b = hundB * 100 + tensB * 10 + onesB;
    } while (a + b < 1000 || a + b > 9999);
  } else {
    // d3: four-digit numbers, multi-carry
    a = randInt(rand, 1000, 4999);
    b = randInt(rand, 1000, 4999);
    // No loop needed; sum of two 4-digit numbers always valid
  }

  const correct = a + b;
  const carryDescription = buildCarryDescription(a, b);

  return {
    kind: 'fill',
    prompt: `${a} + ${b} = ?`,
    correctAnswer: String(correct),
    acceptedAnswers: [String(correct)],
    explanation: `${carryDescription}→ ${a} + ${b} = ${correct}`,
    kp: 'math.calc.addsub-vertical',
  };
}

function buildCarryDescription(a: number, b: number): string {
  const parts: string[] = [];
  let carry = 0;
  const placeNames = ['個', '十', '百', '千'];
  const digitsA = digits(a);
  const digitsB = digits(b);
  const maxLen = Math.max(digitsA.length, digitsB.length);

  for (let i = 0; i < maxLen; i++) {
    const da = digitsA[i] ?? 0;
    const db = digitsB[i] ?? 0;
    const sum = da + db + carry;
    if (sum >= 10) {
      parts.push(`${placeNames[i] ?? '萬'}位 ${da}+${db}${carry > 0 ? `+${carry}` : ''}=${sum}(進位)`);
      carry = Math.floor(sum / 10);
    } else {
      parts.push(`${placeNames[i] ?? '萬'}位 ${da}+${db}${carry > 0 ? `+${carry}` : ''}=${sum}`);
      carry = 0;
    }
  }
  if (carry > 0) parts.push(`最高位進位 ${carry}`);
  return parts.join('; ') + ' ';
}

/** Return array of digits from least significant to most significant */
function digits(n: number): number[] {
  const result: number[] = [];
  if (n === 0) return [0];
  while (n > 0) {
    result.push(n % 10);
    n = Math.floor(n / 10);
  }
  return result;
}
